import { Check, X } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { api, fetchCollection, formatApiError } from "@/lib/api";
import { formatDate } from "@/utils/format";

export function PendingPublications({ user }) {
  const staff = user?.role === "admin" || user?.role === "editor";
  const queryClient = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["admin-chronicles"], queryFn: () => fetchCollection("/admin/chronicles"), enabled: staff });
  const review = useMutation({
    mutationFn: async ({ id, approve }) => (await api.post(`/admin/chronicles/${id}/${approve ? "approve" : "reject"}`)).data,
    onSuccess: (item, variables) => {
      toast.success(variables.approve ? `Publicada: ${item.title}` : `Rechazada: ${item.title}`);
      ["admin-chronicles", "chronicles", "stats"].forEach((key) => queryClient.invalidateQueries({ queryKey: [key] }));
    },
    onError: (error) => toast.error(formatApiError(error)),
  });
  const pending = data.filter((item) => item.status === "pending");
  if (!staff || !pending.length) return null;
  return (
    <div className="mb-8 border border-brass/40 bg-brass/5 p-5" data-testid="admin-pending-publications">
      <p className="eyebrow">Publicaciones pendientes de aprobación ({pending.length})</p>
      <div className="mt-4 grid gap-3">
        {pending.map((item) => (
          <article key={item.id} className="flex flex-col gap-3 border border-white/10 bg-charcoal/60 p-4 md:flex-row md:items-center md:justify-between" data-testid={`admin-pending-publication-${item.id}`}>
            <div className="min-w-0">
              <p className="truncate font-bold text-slate-100">{item.title}</p>
              <p className="text-xs text-slate-400">{item.category} · {formatDate(item.date)} · {item.location}{item.author ? ` · Por ${item.author}` : ""}</p>
              <p className="mt-1 line-clamp-2 text-xs text-slate-500">{item.excerpt}</p>
            </div>
            <div className="flex shrink-0 gap-2">
              <Button type="button" onClick={() => review.mutate({ id: item.id, approve: true })} disabled={review.isPending} className="rounded-none bg-clay font-mono text-[10px] uppercase tracking-[0.18em] text-charcoal hover:bg-brass" data-testid={`admin-approve-publication-${item.id}`}><Check className="h-3 w-3" /> Publicar</Button>
              <Button type="button" variant="outline" onClick={() => review.mutate({ id: item.id, approve: false })} disabled={review.isPending} className="rounded-none border-red-500/25 bg-red-500/5 font-mono text-[10px] uppercase tracking-[0.18em] text-red-200 hover:bg-red-500/20" data-testid={`admin-reject-publication-${item.id}`}><X className="h-3 w-3" /> Rechazar</Button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
